<template>
  <div class="page-container">
    <!-- 顶部标题区 -->
    <div class="page-header">
      <h2 class="page-title">电子面单账号</h2>
      <el-button type="primary" @click="openDialog">
        <el-icon class="el-icon--left"><Plus /></el-icon>
        添加账号
      </el-button>
    </div>

    <!-- 账号列表 -->
    <div class="card-grid" v-if="accountList.length">
      <div class="account-card" v-for="item in accountList" :key="item.id">
        <div class="card-top">
          <!-- 圆形Logo且透明背景 -->
          <el-avatar 
            :size="36" 
            :src="global.expressLogoUrl + item.cpCode + '.png'" 
            shape="circle" 
            style="background-color: transparent;" 
          />
          <div class="card-info">
            <span class="cp-name">{{ item.cpName }}</span>
            <!-- 2. 面单类型居中，且增加上下间隔 -->
            <span class="cp-type">{{ item.expressAccountTypeName }}</span>
          </div>
          <!-- 删除按钮一直显示 -->
          <el-popconfirm title="确定删除此账号吗？" @confirm="handleDelete(item.id)">
            <template #reference>
              <el-button text circle type="danger" class="delete-btn">
                <el-icon><Delete /></el-icon>
              </el-button>
            </template>
          </el-popconfirm>
        </div>
        <div class="card-bottom">
          <!-- 1. 电子面单昵称居左显示 -->
          <span class="nickname">{{ item.nickname }}</span>
          <!-- 余额文字与数额有间隔 -->
          <div class="quota">
            <span class="quota-label">余额</span>
            <!-- 3. 余额数额改成蓝色 -->
            <b class="quota-num">{{ item.quota }}</b>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <el-empty v-else description="暂无电子面单账号" />

    <!-- 添加账号弹窗 -->
    <el-dialog v-model="dialogVisible" title="添加电子面单账号" width="460px" destroy-on-close>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px" size="large">
        <el-form-item label="面单类型" prop="typeCode">
          <el-select v-model="form.typeCode" placeholder="请选择" style="width: 100%;">
            <el-option v-for="t in typeCodeOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="快递公司" prop="cpCode">
          <el-select v-model="form.cpCode" placeholder="请选择" style="width: 100%;">
            <el-option v-for="cp in cpList" :key="cp.value" :label="cp.label" :value="cp.value" />
          </el-select>
        </el-form-item>

        <!-- 动态提示区域 -->
        <el-alert 
          v-if="cpHelpText" 
          :title="cpHelpText" 
          type="info" 
          show-icon 
          :closable="false" 
          style="margin-bottom: 18px; border-radius: 4px;" 
        />

        <el-form-item label="客户编码" prop="account">
          <el-input v-model="form.account" placeholder="请输入客户编码/账号" />
        </el-form-item>
        <el-form-item label="密钥/密码" prop="password">
          <el-input v-model="form.password" type="password" show-password placeholder="请输入对应密钥或密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确认添加</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue';
import { Plus, Delete } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';

// 获取全局实例上的属性
const { proxy } = getCurrentInstance();
const apiService = proxy.apiService;
const global = proxy.global;

// --- 状态定义 ---
const dialogVisible = ref(false);
const submitting = ref(false);
const accountList = ref([]);
const formRef = ref(null);

const form = reactive({
  typeCode: 'WD',
  cpCode: 'ZTO',
  account: '',
  password: ''
});

const rules = {
  typeCode: [{ required: true, message: '请选择面单类型', trigger: 'change' }],
  cpCode: [{ required: true, message: '请选择快递公司', trigger: 'change' }],
  account: [{ required: true, min: 4, max: 30, message: '请输入正确的客户编码', trigger: 'blur' }],
  password: [{ required: true, min: 4, max: 30, message: '请输入正确的密钥/密码', trigger: 'blur' }]
};

const cpList = [
  { label: '中通快递', value: 'ZTO' },
  { label: '圆通速递', value: 'YTO' },
  { label: '极兔速递', value: 'JT' }
];

const typeCodeOptions = [{ label: '网点电子面单', value: 'WD' }];

// 根据选择的快递动态生成提示文案
const cpHelpText = computed(() => {
  if (form.typeCode !== 'WD') return '';
  const tips = {
    ZTO: '请联系合作网点获取客户编码，打单密钥请在客户管理系统中分享名片获取。',
    YTO: '圆通K码请联系网点获取，打印密钥请在网点管家-物料管理中查询。',
    JT: '客户编码/业务员编码请联系合作网点获取，密码为对应编码的登录密码。'
  };
  return tips[form.cpCode] || '';
});

// --- 核心方法 ---
const fetchData = async () => {
  try {
    const res = await apiService.post({ modules: "getExpressAccountList", content: {} });
    if (res.status) {
      accountList.value = res.data.expressAccountList || [];
    }
  } catch (e) {
    console.error('获取账号列表失败', e);
  }
};

const openDialog = () => {
  dialogVisible.value = true;
};

const handleSubmit = async () => {
  await formRef.value.validate();
  submitting.value = true;
  try {
    const cpName = cpList.find(c => c.value === form.cpCode)?.label || '';
    const data = {
      cpCode: form.cpCode,
      orgName: `${cpName}-${form.account}`,
      account: form.account,
      password: form.password
    };
    const res = await apiService.post({ modules: "addExpressAccount", content: data });
    if (res.status) {
      ElMessage.success('添加成功');
      dialogVisible.value = false;
      fetchData(); // 局部刷新列表
    }
  } catch (e) {
    console.error('添加失败', e);
  } finally {
    submitting.value = false;
  }
};

const handleDelete = async (id) => {
  try {
    const res = await apiService.post({ modules: "deleteExpressAccount", content: { expressAccountId: id } });
    if (res.status) {
      ElMessage.success('删除成功');
      accountList.value = accountList.value.filter(item => item.id !== id); // 局部移除
    }
  } catch (e) {
    console.error('删除失败', e);
  }
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.page-container {
  padding: 24px;
  background-color: #ffffff; /* 背景纯白 */
  min-height: 100vh;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1d2129;
  line-height: 1.4;
}

/* 极简网格布局 */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.account-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  transition: box-shadow 0.2s ease;
  border: 1px solid #e5e6eb;
}

.account-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.card-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cp-name {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 2. 面单类型居中，且与快递公司名称有上下间隔 */
.cp-type {
  font-size: 12px;
  color: #86909c;
  margin-top: 8px;
  text-align: center;
}

/* 删除按钮一直显示 */
.delete-btn {
  flex-shrink: 0;
}

.card-bottom {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e5e6eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #86909c;
}

/* 1. 电子面单昵称居左显示 */
.nickname {
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin-right: 12px;
}

/* 余额文字与数额有间隔 */
.quota {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quota-label {
  color: #86909c;
}

/* 3. 余额数额改成蓝色 */
.quota-num {
  font-weight: 600;
  font-size: 16px;
  color: #409eff;
}
</style>